<template>
  <div
    v-on-clickaway="closeDropdown"
    :class="tabClasses"
    data-test-id="tab"
  >
    <a
      ref="tabLink"
      class="tab__link"
      :href="url"
      role="menuitem"
      :aria-haspopup="linkHaspopup"
      :data-track-id="trackId"
      data-test-id="tab-link"
      tabindex="0"
      rel="noopener"
      @click="onClick"
    >
      <EIcon v-if="icon" :icon="icon" size="s" class="tab__icon" />
      <slot />
    </a>
    <EModalDropdown
      v-if="hasDropdown"
      v-accessible-menu
      :visible="dropdownVisible"
      :title="dropdownTitle"
      :size="dropdownSize"
      :side="dropdownSide"
      :class="dropdownClasses"
      :footer-always-shown="dropdownFooterAlwaysShown"
      :aria-label="dropdownTitle"
      data-test-id="tab-dropdown"
      role="menu"
      @close="closeDropdown"
    >
      <slot name="dropdown" />
    </EModalDropdown>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import classes from '~~/common/vue/mixins/classes';
import EModalDropdown from '~~/common/vue/components/modal-dropdown';
import EIcon from '~~/common/vue/components/icon';
/**
 * A navigation element which can either act as a link
 * or open a dropdown containing other links/arbitrary content.
 *
 * Used to switch between search verticals.
 */
export default {
  name: 'ETab',
  components: {
    EModalDropdown,
    EIcon,
  },
  mixins: [clickaway, classes],
  props: {
    url: {
      type: String,
      default: '',
    },
    icon: {
      type: [String, null],
      default: null,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
    dropdownTitle: {
      type: String,
      default: '',
    },
    dropdownSize: {
      type: String,
      default: 'm',
    },
    dropdownSide: {
      type: String,
      default: 'left',
    },
    dropdownFooterAlwaysShown: {
      type: Boolean,
      default: false,
    },
    highlightBar: {
      type: Boolean,
      default: true,
      cssClass: true,
    },
    boldHighlightText: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    // we want the track-id on the link, not the wrapper div
    trackId: {
      type: [String, null],
      default: null,
    },
  },
  data: () => ({
    dropdownVisible: false,
  }),
  computed: {
    hasDropdown() {
      return 'dropdown' in this.$slots;
    },
    tabRole() {
      return this.hasDropdown ? 'menu' : undefined;
    },
    linkHaspopup() {
      return this.hasDropdown ? 'true' : undefined;
    },
    tabClasses() {
      const tabClasses = [...this.classes];
      if (this.dropdownVisible || this.highlighted) {
        tabClasses.push('tab--highlighted');
      }
      return tabClasses;
    },
    dropdownClasses() {
      const dropdownClasses = ['tab__dropdown', `tab__dropdown--side-${this.dropdownSide}`];
      return dropdownClasses;
    },
  },
  watch: {
    dropdownVisible(isVisible) {
      this.$emit('dropdown-visibility-change', isVisible);
    },
  },
  mounted() {
    // if the parent mounts the tab as highlighted,
    // its dropdown should already be open when it mounts
    if (this.highlighted) {
      this.dropdownVisible = true;
    }
  },
  methods: {
    onClick(event) {
      if (!this.hasDropdown && this.url !== '') {
        return;
      }

      event.preventDefault();
      this.dropdownVisible = !this.dropdownVisible;
    },
    closeDropdown() {
      if (this.hasDropdown && this.dropdownVisible) {
        this.dropdownVisible = false;
        this.$refs.tabLink.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$tab-highlight-width: $border-radius-s;

.tab {
  position: relative;
}

.tab__link {
  $padding-bottom: $space-s;

  background: $transparent;
  color: $gray-50;
  cursor: pointer;
  display: flex;
  font-size: $font-s;
  margin: 0;
  padding: $space-s $space-s $padding-bottom;
  transition: color $timing-2s $easing;

  &:hover,
  &:focus {
    color: $color-brand-primary;
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }

  @include desktop {
    $padding-bottom: $space-m;

    padding: $space-m $space-1s $padding-bottom 0;
  }
}

.tab--highlighted {
  .tab__link {
    color: $color-brand-primary;
  }

  &.tab--highlight-bar {
    &::after {
      background-color: $color-border-focus-primary;
      border-radius: $tab-highlight-width $tab-highlight-width 0 0;
      bottom: 0;
      content: '';
      height: $tab-highlight-width;
      left: 0;
      position: absolute;
      right: 0;
    }
  }
}

.tab--bold-highlight-text.tab--highlighted {
  .tab__link {
    font-weight: $font-weight-1l;
  }
}

.tab__icon {
  display: none;
  margin-right: $space-1s;

  @include desktop {
    display: unset;
  }
}

.tab__dropdown {
  @include desktop {
    // non-standard value, design said so
    top: calc(100% - 4px);
    width: auto;

    &.tab__dropdown--side-left {
      left: -$space-s;
    }

    &.tab__dropdown--side-right {
      right: -1 * $space-s;
    }
  }
}
</style>

<docs>
`ETab` can be used in two different modes:

## as a link

```js
<div style="display: flex;">
  <ETab url="http://example.com" icon="search">Example</ETab>
  <ETab url="http://example.com" icon="search" highlighted>Highlighted Example</ETab>
</div>
```

## as a dropdown

```js
<div style="display: flex;">
  <ETab url="http://example.com" icon="search">
    Click me
    <template v-slot:dropdown>
      <ul>
        <li>Some navigation content</li>
        <li>More navigation content</li>
      </ul>
    </template>
  </ETab>
</div>
```
</docs>
